'use client';

import {
	type BrandingFontFamily,
	BRANDING_COLOR_PRESETS,
	deriveBrandColors,
} from '@slims/shared/branding';
import { Check, ImageIcon, Palette, RotateCcw, Save, Type, Upload, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '../../atoms/button/button';
import { Input } from '../../atoms/input/input';
import { Label } from '../../atoms/label/label';
import { Text } from '../../atoms/text/text';
import { cn } from '../../lib/cn';
import { PanelCard, PanelCardTitle } from '../../molecules/panel-card/panel-card';

/**
 * Editor del branding white-label della CER (WB-R22) con **anteprima live**: le
 * modifiche a logo, colori e font si riflettono all'istante nel pannello di
 * anteprima; solo «Salva» le rende persistenti (il chiamante fa la scrittura via
 * callback). I colori dell'anteprima sono derivati con `deriveBrandColors` da
 * `@slims/shared/branding` — lo stesso modulo che veste il portale a runtime —
 * quindi l'anteprima mostra esattamente ciò che vedranno i membri.
 *
 * La vista possiede lo stato di bozza (display state); non fa I/O. Il baseline
 * `value` deve essere stabile fra i render: cambia solo dopo un salvataggio o un
 * ripristino andati a buon fine.
 */

export interface BrandingEditorValues {
	appName: string;
	tagline: string;
	logoUrl: string;
	primaryColor: string;
	secondaryColor: string;
	fontFamily: BrandingFontFamily;
	showSlimsLogoOnLogin: boolean;
}

export interface BrandingEditorViewProps {
	/** Valori persistiti correnti. Stabile fra i render (cambia solo dopo save/reset). */
	value: BrandingEditorValues;
	/** true se la CER ha già un branding salvato — mostra «Ripristina SLIMS». */
	configured: boolean;
	/** Scrittura in corso: disabilita i pulsanti. */
	saving?: boolean;
	/** Nome CER, usato nell'anteprima quando «Nome applicazione» è vuoto. */
	cerName?: string;
	/** Schema colore dell'anteprima e della derivazione colori. Default `dark`. */
	colorScheme?: 'light' | 'dark';
	/**
	 * Carica un file logo e restituisce l'URL pubblico risultante (che la vista
	 * scrive nella bozza `logoUrl`). Se assente resta solo il campo URL manuale.
	 */
	onUploadLogo?: (file: File) => Promise<string>;
	/** Salva i valori di bozza. */
	onSave: (values: BrandingEditorValues) => void;
	/** Ripristina il brand SLIMS CER di default. */
	onReset: () => void;
}

const LOGO_ACCEPT = 'image/png,image/jpeg,image/webp,image/svg+xml';

const FONT_OPTIONS: { value: BrandingFontFamily; label: string; stack: string }[] = [
	{ value: 'inter', label: 'Inter', stack: "'Inter', system-ui, sans-serif" },
	{
		value: 'system',
		label: 'Sistema',
		stack: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
	},
	{ value: 'lora', label: 'Lora', stack: "'Lora', Georgia, serif" },
	{ value: 'roboto', label: 'Roboto', stack: "'Roboto', system-ui, sans-serif" },
	{ value: 'montserrat', label: 'Montserrat', stack: "'Montserrat', system-ui, sans-serif" },
];

const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/** Palette dell'anteprima per lo schema scelto. */
function previewSurface(scheme: 'light' | 'dark') {
	return scheme === 'light'
		? { bg: '#ffffff', panel: '#f7f8fa', border: '#e6e8ec', text: '#0f172a', sub: '#64748b' }
		: { bg: '#0b1220', panel: '#111a2e', border: '#1e2b45', text: '#e8ecf4', sub: '#93a2bd' };
}

export function BrandingEditorView({
	value,
	configured,
	saving = false,
	cerName,
	colorScheme = 'dark',
	onUploadLogo,
	onSave,
	onReset,
}: BrandingEditorViewProps) {
	const [draft, setDraft] = useState<BrandingEditorValues>(value);
	const [uploading, setUploading] = useState(false);
	const fileRef = useRef<HTMLInputElement>(null);

	// Ri-seed della bozza quando cambia il baseline persistito (dopo save/reset).
	const baseKey = JSON.stringify(value);
	const seededKey = useRef(baseKey);
	if (seededKey.current !== baseKey) {
		seededKey.current = baseKey;
		setDraft(value);
	}

	function set<K extends keyof BrandingEditorValues>(k: K, v: BrandingEditorValues[K]) {
		setDraft((prev) => ({ ...prev, [k]: v }));
	}

	async function handleLogoFile(file: File | undefined) {
		if (!file || !onUploadLogo) return;
		setUploading(true);
		try {
			set('logoUrl', await onUploadLogo(file));
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Caricamento del logo non riuscito');
		} finally {
			setUploading(false);
		}
	}

	const dirty = JSON.stringify(draft) !== baseKey;

	const colors = useMemo(
		() =>
			deriveBrandColors(
				{ primaryColor: draft.primaryColor, secondaryColor: draft.secondaryColor },
				colorScheme,
			),
		[draft.primaryColor, draft.secondaryColor, colorScheme],
	);

	const fontStack =
		FONT_OPTIONS.find((f) => f.value === draft.fontFamily)?.stack ??
		"'Inter', system-ui, sans-serif";
	const surf = previewSurface(colorScheme);
	const brandName = draft.appName.trim() || cerName || 'SLIMS CER';

	return (
		<div className="grid gap-6 xl:grid-cols-[minmax(0,32rem)_minmax(0,22rem)] xl:items-start">
			{/* ── Impostazioni ─────────────────────────────────────────── */}
			<div className="min-w-0 space-y-4">
				<PanelCard title={<PanelCardTitle icon={ImageIcon}>Identità</PanelCardTitle>}>
					<div className="space-y-3">
						<Field label="Nome applicazione" htmlFor="brand-app-name">
							<Input
								id="brand-app-name"
								value={draft.appName}
								placeholder="SLIMS CER"
								onChange={(e) => set('appName', e.target.value)}
							/>
						</Field>
						<Field label="Claim / tagline" htmlFor="brand-tagline">
							<Input
								id="brand-tagline"
								value={draft.tagline}
								placeholder="La tua comunità energetica"
								onChange={(e) => set('tagline', e.target.value)}
							/>
						</Field>
						<Field
							label="Logo"
							htmlFor="brand-logo-url"
							hint="Carica un'immagine o incolla l'URL di un asset web pubblico (PNG/SVG, max 5 MB). In assenza resta il wordmark SLIMS."
						>
							<div className="space-y-2">
								<Input
									id="brand-logo-url"
									value={draft.logoUrl}
									placeholder="https://…/logo.png"
									onChange={(e) => set('logoUrl', e.target.value)}
								/>
								{onUploadLogo && (
									<div className="flex flex-wrap items-center gap-2">
										<input
											ref={fileRef}
											type="file"
											accept={LOGO_ACCEPT}
											aria-label="Carica file logo"
											tabIndex={-1}
											className="hidden"
											onChange={(e) => {
												const file = e.target.files?.[0];
												e.target.value = '';
												void handleLogoFile(file);
											}}
										/>
										<Button
											type="button"
											variant="outline"
											size="sm"
											disabled={uploading}
											onClick={() => fileRef.current?.click()}
										>
											<Upload />
											{uploading ? 'Caricamento…' : 'Carica immagine'}
										</Button>
										{draft.logoUrl && (
											<Button
												type="button"
												variant="ghost"
												size="sm"
												disabled={uploading}
												onClick={() => set('logoUrl', '')}
											>
												<X />
												Rimuovi
											</Button>
										)}
									</div>
								)}
							</div>
						</Field>
					</div>
				</PanelCard>

				<PanelCard title={<PanelCardTitle icon={Palette}>Colori</PanelCardTitle>}>
					<div className="space-y-4">
						<ColorField
							label="Colore primario"
							id="brand-primary"
							value={draft.primaryColor}
							onChange={(v) => set('primaryColor', v)}
						/>
						<ColorField
							label="Colore secondario"
							id="brand-secondary"
							value={draft.secondaryColor}
							onChange={(v) => set('secondaryColor', v)}
						/>
					</div>
				</PanelCard>

				<PanelCard title={<PanelCardTitle icon={Type}>Tipografia</PanelCardTitle>}>
					<div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
						{FONT_OPTIONS.map((f) => {
							const selected = draft.fontFamily === f.value;
							return (
								<button
									key={f.value}
									type="button"
									aria-pressed={selected}
									onClick={() => set('fontFamily', f.value)}
									className={cn(
										'flex min-w-0 flex-col items-start gap-1 rounded-lg border px-3 py-2 text-left transition-colors',
										selected
											? 'border-accent bg-accent/10'
											: 'border-border hover:border-accent/50',
									)}
								>
									<span
										className="text-xl leading-none text-text-primary"
										style={{ fontFamily: f.stack }}
									>
										Ag
									</span>
									<span className="w-full truncate text-[11px] text-text-muted">{f.label}</span>
								</button>
							);
						})}
					</div>
				</PanelCard>

				{/* Niente scelta sulla schermata di login: prima dell'accesso l'app non
				    sa di quale CER sia chi entra, quindi mostra sempre il marchio SLIMS
				    (INB-378). */}

				<div className="flex flex-wrap items-center justify-end gap-2">
					{dirty && (
						<Text variant="caption" tone="muted" className="mr-auto">
							Modifiche non salvate
						</Text>
					)}
					{configured && (
						<Button type="button" variant="outline" size="sm" onClick={onReset} disabled={saving}>
							<RotateCcw />
							Ripristina SLIMS
						</Button>
					)}
					<Button
						type="button"
						variant="accent"
						size="sm"
						onClick={() => onSave(draft)}
						disabled={saving || !dirty}
					>
						<Save />
						{saving ? 'Salvataggio…' : 'Salva branding'}
					</Button>
				</div>
			</div>

			{/* ── Anteprima live ───────────────────────────────────────── */}
			<div className="min-w-0 xl:sticky xl:top-4 xl:self-start">
				<PanelCard title={<PanelCardTitle icon={Palette}>Anteprima</PanelCardTitle>}>
					<div
						aria-hidden
						className="overflow-hidden rounded-xl border"
						style={{ background: surf.bg, borderColor: surf.border, fontFamily: fontStack }}
					>
						{/* header */}
						<div
							className="flex items-center justify-between border-b px-3 py-2.5"
							style={{ borderColor: surf.border }}
						>
							<div className="flex min-w-0 items-center gap-2">
								{draft.logoUrl ? (
									<img
										src={draft.logoUrl}
										alt=""
										className="h-7 w-auto max-w-[150px] flex-none object-contain object-left"
									/>
								) : (
									<span
										className="truncate text-[13px] font-extrabold"
										style={{ color: surf.text }}
									>
										{brandName}
									</span>
								)}
							</div>
							<span
								className="h-6 w-6 flex-none rounded-full"
								style={{ background: colors.primaryColorDark }}
							/>
						</div>

						<div className="flex">
							{/* sidebar rail */}
							<div
								className="flex flex-none flex-col gap-2 border-r px-2.5 py-3"
								style={{ borderColor: surf.border }}
							>
								{[0, 1, 2, 3].map((i) => (
									<span
										key={i}
										className="block h-1.5 w-9 rounded-full"
										style={{ background: i === 0 ? colors.primaryColor : surf.border }}
									/>
								))}
							</div>

							{/* main */}
							<div className="min-w-0 flex-1 space-y-3 p-3">
								<div>
									<div className="text-[13px] font-bold" style={{ color: surf.text }}>
										{brandName}
									</div>
									<div className="text-[11px]" style={{ color: surf.sub }}>
										{draft.tagline.trim() || 'La tua comunità energetica'}
									</div>
								</div>

								<div
									className="rounded-lg border p-2.5"
									style={{ background: surf.panel, borderColor: surf.border }}
								>
									<div className="text-[10px] uppercase tracking-wide" style={{ color: surf.sub }}>
										Energia condivisa
									</div>
									<div
										className="text-lg font-extrabold tabular-nums"
										style={{ color: colors.primaryColor }}
									>
										1.248 kWh
									</div>
								</div>

								<div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
									<span
										className="rounded-md px-2.5 py-1 text-[11px] font-semibold"
										style={{ background: colors.primaryColorDark, color: '#ffffff' }}
									>
										Azione
									</span>
									<span
										className="rounded-md border px-2.5 py-1 text-[11px] font-semibold"
										style={{ borderColor: colors.primaryColor, color: colors.primaryColor }}
									>
										Dettagli
									</span>
									<span
										className="text-[11px] font-semibold"
										style={{ color: colors.accentPurple }}
									>
										Vedi tutto →
									</span>
								</div>
							</div>
						</div>
					</div>

					<Text variant="caption" tone="muted" className="mt-2 block">
						Aggiornamento in tempo reale · «Salva» lo applica a tutto il portale e all'app.
					</Text>
				</PanelCard>
			</div>
		</div>
	);
}

// ── sub-componenti ──────────────────────────────────────────────────────────

function Field({
	label,
	htmlFor,
	hint,
	children,
}: {
	label: string;
	htmlFor: string;
	hint?: string;
	children: ReactNode;
}) {
	return (
		<div className="space-y-1">
			<Label htmlFor={htmlFor} className="text-[12px] text-text-muted">
				{label}
			</Label>
			{children}
			{hint && <p className="text-[11px] italic text-text-muted">{hint}</p>}
		</div>
	);
}

function ColorField({
	label,
	id,
	value,
	onChange,
}: {
	label: string;
	id: string;
	value: string;
	onChange: (v: string) => void;
}) {
	const valid = HEX_RE.test(value);
	return (
		<div className="space-y-2">
			<Label htmlFor={id} className="text-[12px] text-text-muted">
				{label}
			</Label>
			<div className="flex flex-wrap items-center gap-1.5">
				{BRANDING_COLOR_PRESETS.map((preset) => {
					const selected = value.toUpperCase() === preset.toUpperCase();
					return (
						<button
							key={preset}
							type="button"
							aria-label={`Usa ${preset}`}
							aria-pressed={selected}
							onClick={() => onChange(preset)}
							className={cn(
								'flex h-7 w-7 flex-none items-center justify-center rounded-full ring-offset-2 ring-offset-card transition-shadow',
								selected && 'ring-2 ring-accent',
							)}
							style={{ background: preset }}
						>
							{selected && <Check className="h-3.5 w-3.5 text-white" aria-hidden />}
						</button>
					);
				})}
			</div>
			<div className="flex items-center gap-2">
				<input
					id={id}
					type="color"
					value={valid ? value : '#000000'}
					onChange={(e) => onChange(e.target.value.toUpperCase())}
					className="h-9 w-12 flex-none cursor-pointer rounded-[7px] border border-border bg-card p-1"
				/>
				<Input
					value={value}
					aria-label={`${label} - valore esadecimale`}
					aria-invalid={!valid}
					placeholder="#00AEEF"
					onChange={(e) => onChange(e.target.value.toUpperCase())}
					className={cn('min-w-0 flex-1 font-mono', !valid && 'border-danger')}
				/>
			</div>
		</div>
	);
}
